import React, { useState, useEffect } from 'react';
import {
  X, ExternalLink, CheckCircle, AlertTriangle, XCircle, Clock,
  RefreshCw, Search, ChevronDown, ChevronUp, Globe, FileText, Ban, FileSearch, Users, Building2
} from 'lucide-react';
import BrandWebsiteTermsTab from './BrandWebsiteTermsTab';

const TermsComparisonModal = ({ isOpen, onClose, project }) => {
  const basePath = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';

  const [activeTab, setActiveTab] = useState('affiliates'); // 'affiliates' or 'brand-website'
  const [affiliates, setAffiliates] = useState([]);
  const [brandTerms, setBrandTerms] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingTerms, setIsCheckingTerms] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && project?.id) {
      fetchAffiliates();
    }
  }, [isOpen, project?.id]);

  const fetchAffiliates = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await fetch(
        `${basePath}/api/terms-checker/${project.id}/affiliates`,
        {
          method: 'GET',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        setAffiliates(data.data.affiliates || []);
        setBrandTerms(data.data.brand?.terms || null);
      } else {
        setError(data.message || 'Failed to fetch affiliates');
      }
    } catch (err) {
      console.error('Error fetching affiliates:', err);
      setError('Failed to fetch affiliates');
    } finally {
      setIsLoading(false);
    }
  };

  const checkAllTerms = async () => {
    setIsCheckingTerms(true);
    try {
      const response = await fetch(
        `${basePath}/api/terms-checker/${project.id}/check-all-terms`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ limit: 10 })
        }
      );

      const data = await response.json();

      if (response.ok && data.success) {
        // Refresh affiliates list
        await fetchAffiliates();
      } else {
        setError(data.message || 'Failed to check terms');
      }
    } catch (err) {
      console.error('Error checking terms:', err);
      setError('Failed to check terms');
    } finally {
      setIsCheckingTerms(false);
    }
  };

  const checkSingleAffiliate = async (affiliateId) => {
    try {
      const response = await fetch(
        `${basePath}/api/terms-checker/affiliate/${affiliateId}/check-terms`,
        {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' }
        }
      );

      if (response.ok) {
        await fetchAffiliates();
      }
    } catch (err) {
      console.error('Error checking affiliate terms:', err);
    }
  };

  const toggleRow = (affiliateId) => {
    setExpandedRows(prev => ({
      ...prev,
      [affiliateId]: !prev[affiliateId]
    }));
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'compliant':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'minor_issues':
        return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
      case 'major_issues':
      case 'outdated':
        return <XCircle className="w-5 h-5 text-red-500" />;
      case 'brand_inactive':
        return <Ban className="w-5 h-5 text-orange-500" />;
      case 'brand_not_found':
        return <FileSearch className="w-5 h-5 text-purple-500" />;
      case 'pending':
      case 'check_failed':
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      compliant: 'bg-green-100 text-green-700 border-green-200',
      minor_issues: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      major_issues: 'bg-red-100 text-red-700 border-red-200',
      outdated: 'bg-orange-100 text-orange-700 border-orange-200',
      pending: 'bg-gray-100 text-gray-600 border-gray-200',
      check_failed: 'bg-gray-100 text-gray-600 border-gray-200',
      brand_not_found: 'bg-purple-100 text-purple-600 border-purple-200',
      brand_inactive: 'bg-orange-100 text-orange-700 border-orange-200'
    };

    const labels = {
      compliant: 'Compliant',
      minor_issues: 'Minor Issues',
      major_issues: 'Terms Not Mentioned',
      outdated: 'Outdated',
      pending: 'Pending',
      check_failed: 'Check Failed',
      brand_not_found: 'Brand Not Found',
      brand_inactive: 'Brand Inactive'
    };

    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full border ${styles[status] || styles.pending}`}>
        {labels[status] || 'Unknown'}
      </span>
    );
  };

  // Parse vertical terms for display
  const parseVerticalTerms = (terms) => {
    if (!terms) return {};
    if (typeof terms === 'string') {
      try { return JSON.parse(terms); } catch { return {}; }
    }
    return terms;
  };

  const brandVerticalTerms = parseVerticalTerms(brandTerms?.verticalTerms);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="absolute inset-4 md:inset-8 lg:inset-12 bg-white rounded-xl shadow-2xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="border-b border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between px-6 py-4">
            <div className="flex items-center gap-3">
              <FileText className="w-6 h-6 text-blue-600" />
              <div>
                <h2 className="text-lg font-semibold text-gray-800">
                  Terms Comparison - {project?.brand_name}
                </h2>
                <p className="text-sm text-gray-500">
                  Compare affiliate terms with your official terms
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {activeTab === 'affiliates' && (
                <button
                  onClick={checkAllTerms}
                  disabled={isCheckingTerms}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 disabled:opacity-50"
                >
                  {isCheckingTerms ? (
                    <>
                      <RefreshCw className="w-4 h-4 animate-spin" />
                      Checking...
                    </>
                  ) : (
                    <>
                      <Search className="w-4 h-4" />
                      Check All Terms
                    </>
                  )}
                </button>
              )}
              <button
                onClick={onClose}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex px-6 gap-1">
            <button
              onClick={() => setActiveTab('affiliates')}
              className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors flex items-center gap-2 ${
                activeTab === 'affiliates'
                  ? 'bg-white text-blue-600 border-t border-l border-r border-gray-200'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Users className="w-4 h-4" />
              Affiliate Terms
              <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                activeTab === 'affiliates' ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-600'
              }`}>
                {affiliates.length}
              </span>
            </button>
            <button
              onClick={() => setActiveTab('brand-website')}
              className={`px-4 py-2.5 text-sm font-medium rounded-t-lg transition-colors flex items-center gap-2 ${
                activeTab === 'brand-website'
                  ? 'bg-white text-blue-600 border-t border-l border-r border-gray-200'
                  : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Building2 className="w-4 h-4" />
              Your Website
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6">
          {activeTab === 'brand-website' ? (
            <BrandWebsiteTermsTab project={project} />
          ) : isLoading ? (
            <div className="flex items-center justify-center h-64">
              <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
              <span className="ml-3 text-gray-600">Loading affiliates...</span>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                <p className="text-gray-600">{error}</p>
                <button
                  onClick={fetchAffiliates}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Retry
                </button>
              </div>
            </div>
          ) : affiliates.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <Globe className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600">No affiliates found for this brand</p>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              {affiliates.map((affiliate) => {
                const isExpanded = expandedRows[affiliate.id];
                const affiliateTerms = typeof affiliate.affiliate_displayed_terms === 'string'
                  ? JSON.parse(affiliate.affiliate_displayed_terms || '{}')
                  : (affiliate.affiliate_displayed_terms || {});
                const comparison = typeof affiliate.terms_comparison === 'string'
                  ? JSON.parse(affiliate.terms_comparison || '{}')
                  : (affiliate.terms_comparison || {});
                const discrepancies = typeof affiliate.discrepancies === 'string'
                  ? JSON.parse(affiliate.discrepancies || '[]')
                  : (affiliate.discrepancies || []);

                // Get the actual page URL where brand was found (from brand_page_urls or affiliate_url)
                const brandPageUrls = typeof affiliate.brand_page_urls === 'string'
                  ? JSON.parse(affiliate.brand_page_urls || '[]')
                  : (affiliate.brand_page_urls || []);
                const displayPageUrl = brandPageUrls.length > 0 ? brandPageUrls[0] : affiliate.affiliate_url;

                return (
                  <div
                    key={affiliate.id}
                    className="bg-white border border-gray-200 rounded-lg overflow-hidden"
                  >
                    {/* Row Header */}
                    <div
                      className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-gray-50"
                      onClick={() => toggleRow(affiliate.id)}
                    >
                      <div className="flex items-center gap-3">
                        {getStatusIcon(affiliate.compliance_status)}
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-gray-800">
                              {affiliate.affiliate_domain || new URL(affiliate.affiliate_url).hostname}
                            </span>
                            <a
                              href={displayPageUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          </div>
                          <p className="text-xs text-gray-500 truncate max-w-md">
                            {displayPageUrl}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3">
                        {getStatusBadge(affiliate.compliance_status)}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            checkSingleAffiliate(affiliate.id);
                          }}
                          className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                          title="Re-check terms"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </button>
                        {isExpanded ? (
                          <ChevronUp className="w-5 h-5 text-gray-400" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400" />
                        )}
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {isExpanded && (
                      <div className="px-4 py-4 border-t border-gray-100 bg-gray-50">
                        {affiliate.compliance_status === 'pending' || !affiliate.terms_checked_at ? (
                          <p className="text-gray-500 text-sm text-center py-4">
                            Terms not checked yet. Click the refresh button to check.
                          </p>
                        ) : affiliate.compliance_status === 'brand_inactive' ? (
                          // Brand Inactive Status
                          <div className="bg-orange-50 p-4 rounded-lg border border-orange-200">
                            <div className="flex items-center gap-2 mb-2">
                              <Ban className="w-5 h-5 text-orange-600" />
                              <h4 className="text-sm font-medium text-orange-800">Brand No Longer Active</h4>
                            </div>
                            <p className="text-sm text-orange-700">
                              {affiliate.inactive_reason || affiliateTerms.inactiveReason || 'This brand is no longer listed or promoted on this affiliate site.'}
                            </p>
                            {affiliateTerms.analysisNotes && (
                              <p className="text-xs text-orange-600 mt-2">{affiliateTerms.analysisNotes}</p>
                            )}
                            {affiliate.pages_checked > 0 && (
                              <p className="text-xs text-gray-500 mt-3">
                                Checked {affiliate.pages_checked} page{affiliate.pages_checked !== 1 ? 's' : ''} on this affiliate site
                              </p>
                            )}
                          </div>
                        ) : affiliate.compliance_status === 'brand_not_found' ? (
                          // Brand Not Found Status
                          <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                            <div className="flex items-center gap-2 mb-2">
                              <FileSearch className="w-5 h-5 text-purple-600" />
                              <h4 className="text-sm font-medium text-purple-800">Brand Not Found</h4>
                            </div>
                            <p className="text-sm text-purple-700">
                              No content related to this brand was found on the affiliate site.
                            </p>
                            {affiliate.pages_checked > 0 && (
                              <p className="text-xs text-gray-500 mt-3">
                                Searched {affiliate.pages_checked} page{affiliate.pages_checked !== 1 ? 's' : ''} on this affiliate site
                              </p>
                            )}
                          </div>
                        ) : (
                          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                            {/* Pages Checked Info */}
                            {affiliate.pages_checked > 0 && (
                              <div className="lg:col-span-2 bg-blue-50 px-3 py-2 rounded-lg border border-blue-100 flex items-center gap-2 text-sm text-blue-700">
                                <FileSearch className="w-4 h-4" />
                                <span>Analyzed {affiliate.pages_checked} page{affiliate.pages_checked !== 1 ? 's' : ''} on this affiliate site</span>
                              </div>
                            )}

                            {/* Your Terms */}
                            <div className="bg-white p-4 rounded-lg border border-gray-200">
                              <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                                <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                                Your Official Terms
                              </h4>
                              {Object.entries(brandVerticalTerms).map(([vertical, terms]) => (
                                terms && terms.trim() && (
                                  <div key={vertical} className="mb-2">
                                    <span className="text-xs font-medium text-blue-600 uppercase">{vertical}</span>
                                    <p className="text-sm text-gray-600 mt-1">{terms}</p>
                                  </div>
                                )
                              ))}
                            </div>

                            {/* Affiliate Terms */}
                            <div className="bg-white p-4 rounded-lg border border-gray-200">
                              <h4 className="text-sm font-medium text-gray-700 mb-3 flex items-center gap-2">
                                <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                                Affiliate Displayed Terms
                              </h4>
                              {affiliateTerms.brandFound === false ? (
                                <p className="text-gray-500 text-sm">Brand not found on affiliate page</p>
                              ) : (
                                <div className="space-y-2 text-sm">
                                  {/* Offer - displayed prominently */}
                                  {affiliateTerms.offer && (
                                    <div className="bg-purple-50 p-2 rounded border border-purple-200 mb-2">
                                      <span className="font-medium text-purple-700">Offer:</span>
                                      <p className="text-purple-800">{affiliateTerms.offer}</p>
                                    </div>
                                  )}
                                  {affiliateTerms.welcomeBonus && (
                                    <div><span className="font-medium">Welcome Bonus:</span> {affiliateTerms.welcomeBonus}</div>
                                  )}
                                  {affiliateTerms.wageringRequirement && (
                                    <div><span className="font-medium">Wagering:</span> {affiliateTerms.wageringRequirement}</div>
                                  )}
                                  {affiliateTerms.minimumDeposit && (
                                    <div><span className="font-medium">Min Deposit:</span> {affiliateTerms.minimumDeposit}</div>
                                  )}
                                  {affiliateTerms.maximumBonus && (
                                    <div><span className="font-medium">Max Bonus:</span> {affiliateTerms.maximumBonus}</div>
                                  )}
                                  {affiliateTerms.freeSpins && (
                                    <div><span className="font-medium">Free Spins:</span> {affiliateTerms.freeSpins.count} {affiliateTerms.freeSpins.value && `@ ${affiliateTerms.freeSpins.value}`}</div>
                                  )}
                                  {affiliateTerms.timeLimit && (
                                    <div><span className="font-medium">Time Limit:</span> {affiliateTerms.timeLimit}</div>
                                  )}
                                  {affiliateTerms.maxBet && (
                                    <div><span className="font-medium">Max Bet:</span> {affiliateTerms.maxBet}</div>
                                  )}
                                  {affiliateTerms.maxConversion && (
                                    <div><span className="font-medium">Max Conversion:</span> {affiliateTerms.maxConversion}</div>
                                  )}
                                  {affiliateTerms.playerRestrictions && (
                                    <div><span className="font-medium">Restrictions:</span> {affiliateTerms.playerRestrictions}</div>
                                  )}
                                  {affiliateTerms.fullTermsText && (
                                    <div className="mt-2 pt-2 border-t border-gray-100">
                                      <span className="font-medium">Full Terms:</span>
                                      <p className="text-gray-600 mt-1 whitespace-pre-wrap">{affiliateTerms.fullTermsText}</p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>

                            {/* Discrepancies */}
                            {discrepancies.length > 0 && (
                              <div className="lg:col-span-2 bg-red-50 p-4 rounded-lg border border-red-200">
                                <h4 className="text-sm font-medium text-red-700 mb-3">Discrepancies Found</h4>
                                <div className="space-y-2">
                                  {discrepancies.map((disc, idx) => (
                                    <div key={idx} className="flex items-start gap-2 text-sm">
                                      <AlertTriangle className={`w-4 h-4 mt-0.5 ${disc.severity === 'major' ? 'text-red-500' : 'text-yellow-500'}`} />
                                      <div>
                                        <span className="font-medium">{disc.term}:</span>
                                        <span className="text-gray-600"> Official: {disc.official} → Affiliate: {disc.affiliate}</span>
                                        <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${disc.severity === 'major' ? 'bg-red-100 text-red-600' : 'bg-yellow-100 text-yellow-600'}`}>
                                          {disc.severity}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Comparison Summary */}
                            {comparison.summary && (
                              <div className="lg:col-span-2 bg-gray-100 p-4 rounded-lg">
                                <h4 className="text-sm font-medium text-gray-700 mb-2">Summary</h4>
                                <p className="text-sm text-gray-600">{comparison.summary}</p>
                                {comparison.recommendedActions && comparison.recommendedActions.length > 0 && (
                                  <div className="mt-2">
                                    <span className="text-xs font-medium text-gray-500">Recommended Actions:</span>
                                    <ul className="list-disc list-inside text-sm text-gray-600 mt-1">
                                      {comparison.recommendedActions.map((action, idx) => (
                                        <li key={idx}>{action}</li>
                                      ))}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        )}

                        {affiliate.terms_checked_at && (
                          <p className="text-xs text-gray-400 mt-3 text-right">
                            Last checked: {new Date(affiliate.terms_checked_at).toLocaleString()}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          <span className="text-sm text-gray-500">
            {affiliates.length} affiliate{affiliates.length !== 1 ? 's' : ''} found
          </span>
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 hover:text-gray-800 border border-gray-200 rounded-lg hover:bg-gray-100 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsComparisonModal;
